import React, { useState, useEffect, useCallback, useRef, useMemo, memo, lazy, Suspense } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import SimpleAudioPlayer from '../components/SimpleAudioPlayer';
import StarRating from '../components/StarRating';
import FolderTree from '../components/FolderTree';
import CratePanel from '../components/CratePanel';
import HotPicks from '../components/HotPicks';
import DownloadQueue from '../components/DownloadQueue';
import CamelotWheel, { KeyBadge } from '../components/CamelotWheel';
import FullPackCard from '../components/FullPackCard';
import DownloadHistory from '../components/DownloadHistory';
import MusicRequestForm from '../components/MusicRequestForm';
import BPMRangeFilter from '../components/BPMRangeFilter';
import CuratedPlaylists from '../components/CuratedPlaylists';
import MySubscriptionModal from '../components/MySubscriptionModal';
import PaymentMethodIcons from '../components/PaymentMethodIcons';

// Pagination constants - load more tracks per page for better UX
const PAGE_SIZE = 200;

// Download Playlist Button Component
const DownloadPlaylistButton = ({ playlistId, playlistName, trackCount }) => {
  const [downloading, setDownloading] = useState(false);
  
  const handleDownloadPlaylist = async () => {
    if (trackCount === 0) {
      alert('No hay tracks en esta playlist para descargar');
      return;
    }
    
    if (!window.confirm(`¿Descargar "${playlistName}" como ZIP?\n\nSe creará un archivo ZIP con ${trackCount} tracks.\nEsto puede tomar varios segundos.`)) {
      return;
    }
    
    try {
      setDownloading(true);
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/downloads/playlist?playlist_id=${encodeURIComponent(playlistId)}`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            ...(localStorage.getItem('wtpl_session_token') ? { 'Authorization': `Bearer ${localStorage.getItem('wtpl_session_token')}` } : {})
          }
        }
      );
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Error al descargar playlist');
      }
      
      // Get filename from headers
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `WTPL_${playlistName.replace(/[^a-zA-Z0-9]/g, '_')}.zip`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) filename = match[1];
      }
      
      // Download the ZIP
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      alert(`Descarga completada!\n\n${filename}`);
      
    } catch (error) {
      console.error('Error downloading playlist:', error);
      alert('Error al descargar playlist: ' + error.message);
    } finally {
      setDownloading(false);
    }
  };
  
  return (
    <button
      onClick={handleDownloadPlaylist}
      disabled={downloading || trackCount === 0}
      className="flex items-center gap-2 bg-white/20 hover:bg-white/30 text-white font-semibold px-3 py-1.5 rounded-lg text-xs transition-all disabled:opacity-50"
      data-testid="download-playlist-btn"
    >
      {downloading ? (
        <>
          <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
          </svg>
          Preparando...
        </>
      ) : (
        <>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
          </svg>
          Descargar ZIP
        </>
      )}
    </button>
  );
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Dashboard = ({ user: initialUser }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [user, setUser] = useState(initialUser || null);
  const [tracks, setTracks] = useState([]);
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [verifyingPayment, setVerifyingPayment] = useState(false);
  const [downloadingFolder, setDownloadingFolder] = useState(false);
  const [showRequestForm, setShowRequestForm] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalTracks, setTotalTracks] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [hasMoreTracks, setHasMoreTracks] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const trackListRef = useRef(null);
  
  // Dashboard state (last refresh, processing status)
  const [dashboardState, setDashboardState] = useState(null);
  const [refreshingDashboard, setRefreshingDashboard] = useState(false);
  const [processingStatus, setProcessingStatus] = useState(null);
  
  // Audio player state
  const [currentTrack, setCurrentTrack] = useState(null);
  
  // Ratings state
  const [userRatings, setUserRatings] = useState({});
  const [trackRatings, setTrackRatings] = useState({});
  
  // Top rated tracks
  const [topRatedTracks, setTopRatedTracks] = useState([]);
  
  // Crate panel state
  const [showCratePanel, setShowCratePanel] = useState(false);
  const [selectedTrackForCrate, setSelectedTrackForCrate] = useState(null);
  
  // Quick-add to playlist state (admin only)
  const [quickAddTrackId, setQuickAddTrackId] = useState(null);
  const [curatedPlaylists, setCuratedPlaylists] = useState([]);
  const [addingToPlaylist, setAddingToPlaylist] = useState(null);
  
  // Download queue state
  const [selectedTracks, setSelectedTracks] = useState([]);
  
  // Version filter state
  const [versionFilter, setVersionFilter] = useState('');
  const [availableVersions, setAvailableVersions] = useState([]);
  
  // Tag filter state
  const [activeTag, setActiveTag] = useState('');
  const [tagCounts, setTagCounts] = useState({});
  const [trendingTrackIds, setTrendingTrackIds] = useState(new Set());
  
  // Key (Camelot) filter state
  const [keyFilter, setKeyFilter] = useState('');
  const [availableKeys, setAvailableKeys] = useState([]);
  const [showCamelotWheel, setShowCamelotWheel] = useState(false);

  // BPM Range filter state
  const [bpmRange, setBpmRange] = useState([60, 200]);
  const [bpmFilterActive, setBpmFilterActive] = useState(false);

  // Sort state
  const [sortBy, setSortBy] = useState('newest');

  // Curated Playlist state
  const [selectedPlaylist, setSelectedPlaylist] = useState(null);

  // Hide downloaded tracks state
  const [hideDownloaded, setHideDownloaded] = useState(false);
  const [downloadedTrackIds, setDownloadedTrackIds] = useState(new Set());

  // Favorites state
  const [favoriteTrackIds, setFavoriteTrackIds] = useState(new Set());
  const [showOnlyFavorites, setShowOnlyFavorites] = useState(false);
  const [togglingFavorite, setTogglingFavorite] = useState(null);

  // Collapsible date groups (persisted in localStorage so toggle survives navigation)
  const [collapsedDateGroups, setCollapsedDateGroups] = useState(() => {
    try {
      const raw = localStorage.getItem('wtpl_collapsed_dates');
      return raw ? new Set(JSON.parse(raw)) : new Set();
    } catch { return new Set(); }
  });
  const toggleDateGroup = (key) => {
    setCollapsedDateGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      try { localStorage.setItem('wtpl_collapsed_dates', JSON.stringify(Array.from(next))); } catch { /* ignore quota errors */ }
      return next;
    });
  };

  // My Subscription modal
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);

  // Full Pack purchase state
  const [fullPackPurchased, setFullPackPurchased] = useState(false);
  const [verifyingFullPack, setVerifyingFullPack] = useState(false);
  
  // Starter Pack purchase state
  const [starterPackPurchased, setStarterPackPurchased] = useState(false);
  const [verifyingStarterPack, setVerifyingStarterPack] = useState(false);
  
  // Download history state
  const [showDownloadHistory, setShowDownloadHistory] = useState(false);

  useEffect(() => {
    if (user) {
      // Check if returning from Stripe with session_id (subscription)
      const sessionId = searchParams.get('session_id');
      // Check if returning from Full Pack purchase
      const fullpackSession = searchParams.get('fullpack_session');
      const fullpackSuccess = searchParams.get('success');
      const fullpackCancelled = searchParams.get('fullpack_cancelled');
      // Check if returning from Starter Pack purchase
      const starterpackSession = searchParams.get('starterpack_session');
      const starterpackCancelled = searchParams.get('starterpack_cancelled');
      
      if (fullpackSession && fullpackSuccess === 'true') {
        verifyFullPackPayment(fullpackSession);
      } else if (starterpackSession && fullpackSuccess === 'true') {
        verifyStarterPackPayment(starterpackSession);
      } else if (fullpackCancelled || starterpackCancelled) {
        // User cancelled the payment
        navigate('/dashboard', { replace: true });
        fetchData();
      } else if (sessionId) {
        verifyPayment(sessionId);
      } else {
        fetchData();
      }
    }
  }, [user, searchParams]);

  const verifyPayment = async (sessionId) => {
    try {
      setVerifyingPayment(true);
      setLoading(true);
      
      // Check checkout status with Stripe
      const response = await api.get(
        `/subscriptions/checkout-status/${sessionId}`,
        { withCredentials: true }
      );
      
      console.debug('Payment verification result:', response.data);

      // Note: checkout_completed funnel event is recorded server-side via Stripe webhook

      // Clear the session_id from URL
      navigate('/dashboard', { replace: true });
      
      // Fetch data after verification
      await fetchData();
    } catch (error) {
      console.error('Error verifying payment:', error);
      // Clear the URL parameters even on error
      navigate('/dashboard', { replace: true });
      // Still try to fetch data even if verification fails
      await fetchData();
    } finally {
      setVerifyingPayment(false);
    }
  };

  const verifyFullPackPayment = async (sessionId) => {
    try {
      setVerifyingFullPack(true);
      setLoading(true);
      
      // Verify the Full Pack purchase with Stripe
      const response = await api.get(
        `/fullpack/verify/${sessionId}`,
        { withCredentials: true }
      );
      
      console.debug('Full Pack verification result:', response.data);
      
      if (response.data.verified) {
        setFullPackPurchased(true);
        // Show success message
        alert('¡Felicidades! Tu compra del WTPL Full Pack ha sido verificada. Ya puedes descargarlo.');
      }
      
      // Clear the URL parameters
      navigate('/dashboard', { replace: true });
      
      // Fetch data after verification
      await fetchData();
    } catch (error) {
      console.error('Error verifying Full Pack payment:', error);
      // Clear the URL parameters even on error
      navigate('/dashboard', { replace: true });
      // Still try to fetch data even if verification fails
      await fetchData();
    } finally {
      setVerifyingFullPack(false);
    }
  };

  const verifyStarterPackPayment = async (sessionId) => {
    try {
      setVerifyingStarterPack(true);
      setLoading(true);
      
      // Verify the Starter Pack purchase with Stripe
      const response = await api.get(
        `/starterpack/verify/${sessionId}`,
        { withCredentials: true }
      );
      
      console.debug('Starter Pack verification result:', response.data);
      
      if (response.data.verified) {
        setStarterPackPurchased(true);
        // Show success message
        alert('¡Felicidades! Tu compra del WTPL Starter Pack ha sido verificada. Ya puedes descargarlo.');
      }
      
      // Clear the URL parameters
      navigate('/dashboard', { replace: true });
      
      // Fetch data after verification
      await fetchData();
    } catch (error) {
      console.error('Error verifying Starter Pack payment:', error);
      // Clear the URL parameters even on error
      navigate('/dashboard', { replace: true });
      // Still try to fetch data even if verification fails
      await fetchData();
    } finally {
      setVerifyingStarterPack(false);
    }
  };

  const fetchData = async (resetTracks = true) => {
    try {
      if (resetTracks) {
        setLoading(true);
        setCurrentPage(1);
      }
      
      // Fetch subscription status
      const statusRes = await api.get(`/subscriptions/status`, {
        withCredentials: true,
      });
      setSubscriptionStatus(statusRes.data);

      // Fetch folders
      const foldersRes = await api.get(`/folders`, {
        withCredentials: true,
      });
      setFolders(foldersRes.data);

      // Fetch tracks with pagination - initial load
      await fetchTracks(1, resetTracks);
      
      // Fetch dashboard state (last refresh time)
      try {
        const stateRes = await api.get(`/dashboard/state`, {
          withCredentials: true,
        });
        setDashboardState(stateRes.data);
      } catch (e) {
        // Dashboard state endpoint may not be available; non-fatal
        console.warn('[Dashboard] dashboard/state fetch failed:', e?.message || e);
      }
      
      // Fetch processing status
      try {
        const processingRes = await api.get(`/tracks/processing-status`, {
          withCredentials: true,
        });
        setProcessingStatus(processingRes.data);
      } catch (e) {
        // Processing-status endpoint is non-critical; log only unexpected errors
        if (e?.response?.status !== 401 && e?.response?.status !== 404) {
          console.warn('[Dashboard] processing-status fetch failed:', e?.message || e);
        }
      }
      
      // Fetch user's ratings
      try {
        const ratingsRes = await api.get(`/ratings`, {
          withCredentials: true,
        });
        setUserRatings(ratingsRes.data);
      } catch (e) {
        // Ratings endpoint is non-critical; log only unexpected errors
        if (e?.response?.status !== 401 && e?.response?.status !== 404) {
          console.warn('[Dashboard] ratings fetch failed:', e?.message || e);
        }
      }
      
      // Fetch top rated tracks
      try {
        const topRatedRes = await api.get(`/tracks/top-rated?limit=10`, {
          withCredentials: true,
        });
        setTopRatedTracks(topRatedRes.data);
      } catch (e) {
        console.debug('[Dashboard] top-rated tracks unavailable:', e?.message || e);
      }
      
      // Fetch available versions for filter
      try {
        const versionsRes = await api.get(`/track-versions`, {
          withCredentials: true,
        });
        setAvailableVersions(versionsRes.data);
      } catch (e) {
        console.debug('[Dashboard] track versions unavailable:', e?.message || e);
      }
      
      // Fetch tag counts for quick filters
      try {
        const tagRes = await api.get(`/track-tag-counts`, {
          withCredentials: true,
          params: selectedFolder ? { folder: selectedFolder } : {}
        });
        setTagCounts(tagRes.data.tags || {});
        setTrendingTrackIds(new Set(tagRes.data.trending_track_ids || []));
      } catch (e) {
        // Tags not available yet
      }
      
      // Fetch available keys for filter
      try {
        const keysRes = await api.get(`/track-keys`, {
          withCredentials: true,
        });
        setAvailableKeys(keysRes.data);
      } catch (e) {
        // Keys not available yet
      }
      
      // Fetch downloaded track IDs for hide filter
      try {
        const downloadedRes = await api.get(`/downloads/track-ids`, {
          withCredentials: true,
        });
        setDownloadedTrackIds(new Set(downloadedRes.data.track_ids));
      } catch (e) {
        // Downloads not available yet
      }
      
      // Fetch favorite track IDs
      try {
        const favoritesRes = await api.get(`/favorites`, {
          withCredentials: true,
        });
        setFavoriteTrackIds(new Set(favoritesRes.data.track_ids));
      } catch (e) {
        // Favorites not available yet
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 403) {
        // No active subscription
        setSubscriptionStatus({ subscription_status: 'inactive' });
      }
    } finally {
      setLoading(false);
    }
  };

  // Manual dashboard refresh (Admin only)
  const handleRefreshDashboard = async () => {
    if (refreshingDashboard) return;
    
    try {
      setRefreshingDashboard(true);
      
      const response = await api.post(`/dashboard/refresh`, {}, {
        withCredentials: true,
      });
      
      if (response.data.success) {
        setDashboardState(response.data.state);
        // Refetch tracks with updated data
        await fetchTracks(1, true);
        alert(`${response.data.message}`);
      }
    } catch (error) {
      console.error('Error refreshing dashboard:', error);
      if (error.response?.status === 403) {
        alert('⚠️ Se requiere verificación de PIN de admin');
      } else {
        alert('Error al actualizar el dashboard. Intenta de nuevo.');
      }
    } finally {
      setRefreshingDashboard(false);
    }
  };

  // Check if track is ready for playback
  const isTrackReady = (track) => {
    return !track.processing_status || 
           track.processing_status === 'ready' || 
           track.processing_status === null;
  };

  // Fetch tracks with pagination
  const fetchTracks = async (page = 1, reset = false) => {
    try {
      if (page > 1) {
        setLoadingMore(true);
      }

      // Build query params for server-side filtering
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('page_size', PAGE_SIZE.toString());
      
      if (selectedFolder) {
        params.append('folder', selectedFolder);
      }
      if (activeTag) {
        params.append('tag', activeTag);
      } else if (versionFilter) {
        params.append('version', versionFilter);
      }
      if (keyFilter) {
        params.append('key', keyFilter);
      }
      if (bpmFilterActive && bpmRange[0] !== 60) {
        params.append('bpm_min', bpmRange[0].toString());
      }
      if (bpmFilterActive && bpmRange[1] !== 200) {
        params.append('bpm_max', bpmRange[1].toString());
      }
      if (searchQuery) {
        params.append('search', searchQuery);
      }
      if (sortBy && sortBy !== 'newest') {
        params.append('sort_by', sortBy);
      }

      const tracksRes = await api.get(`/tracks/paginated?${params.toString()}`, {
        withCredentials: true,
      });

      const { tracks: newTracks, pagination } = tracksRes.data;
      
      if (reset || page === 1) {
        setTracks(newTracks);
      } else {
        setTracks(prev => [...prev, ...newTracks]);
      }
      
      setCurrentPage(pagination.page);
      setTotalTracks(pagination.total_count);
      setTotalPages(pagination.total_pages);
      setHasMoreTracks(pagination.has_next);

    } catch (error) {
      console.error('Error fetching tracks:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  // Load more tracks (infinite scroll) - DEPRECATED, using pagination instead
  const loadMoreTracks = useCallback(() => {
    if (!loadingMore && hasMoreTracks) {
      fetchTracks(currentPage + 1, false);
    }
  }, [loadingMore, hasMoreTracks, currentPage]);

  // Go to specific page (traditional pagination)
  const goToPage = useCallback((page) => {
    if (page >= 1 && page <= totalPages && page !== currentPage && !loadingMore) {
      fetchTracks(page, true);
      // Scroll to top of track list
      window.scrollTo({ top: 300, behavior: 'smooth' });
    }
  }, [totalPages, currentPage, loadingMore]);

  // Generate page numbers for pagination
  const getPageNumbers = useCallback(() => {
    const pages = [];
    const maxVisible = 5;
    
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    return pages;
  }, [currentPage, totalPages]);

  // Effect to refetch tracks when filters change
  // Skip refetch when a curated playlist is selected (it has its own tracks)
  useEffect(() => {
    if (selectedPlaylist) {
      return; // Don't refetch when a curated playlist is selected
    }
    if (subscriptionStatus?.subscription_status === 'active' || user?.email === 'ydeliofleitas961204@gmail.com') {
      fetchTracks(1, true);
    }
  }, [selectedFolder, versionFilter, activeTag, keyFilter, bpmFilterActive, bpmRange, searchQuery, selectedPlaylist, sortBy]);

  const handleLogout = async () => {
    try {
      await api.post(`/auth/logout`, {}, { withCredentials: true });
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
      navigate('/');
    }
  };

  const handleSubscribe = async () => {
    try {
      setIsProcessingPayment(true);
      
      // Create Stripe checkout session
      const response = await api.post(
        `/subscriptions/create-checkout`,
        {},
        { 
          withCredentials: true,
          timeout: 30000 // 30 second timeout for mobile
        }
      );
      
      // Redirect to Stripe checkout
      if (response.data.url) {
        console.debug('[Subscribe] Redirecting to Stripe:', response.data.url);
        // Use window.location.assign for better mobile compatibility
        setTimeout(() => {
          window.location.assign(response.data.url);
        }, 100);
      } else {
        throw new Error('No se recibió URL de pago');
      }
    } catch (error) {
      console.error('Subscription error:', error);
      setIsProcessingPayment(false);
      
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        alert('La conexión tardó demasiado. Por favor verifica tu conexión a internet e intenta de nuevo.');
      } else if (error.response?.data?.detail) {
        alert(`Error: ${error.response.data.detail}`);
      } else {
        alert('Error al procesar la suscripción. Por favor intenta de nuevo.');
      }
    }
  };

  const handleDownload = async (track) => {
    // Check if track is ready for download
    if (!isTrackReady(track)) {
      alert('Este track aún está siendo procesado. No disponible para descarga.');
      return;
    }
    
    try {
      // Register download
      await api.post(
        `/downloads`,
        { track_id: track.track_id },
        { withCredentials: true }
      );

      // Add to downloaded tracks set
      setDownloadedTrackIds(prev => new Set([...prev, track.track_id]));

      // Open Google Drive link
      window.open(track.drive_link, '_blank');
    } catch (error) {
      console.error('Download error:', error);
      alert('Error al registrar la descarga');
    }
  };

  const handlePlayTrack = (track) => {
    // Check if track is ready for playback
    if (!isTrackReady(track)) {
      alert('Este track aún está siendo procesado. Intenta de nuevo más tarde.');
      return;
    }
    
    // Use our backend streaming endpoint
    const fileId = track.file_id;
    
    if (fileId) {
      // Use our backend proxy for streaming
      const streamUrl = `/stream/${fileId}`;
      
      // Generate unique key to force component remount when changing tracks
      const playKey = `${track.track_id}_${Date.now()}`;
      
      setCurrentTrack({
        ...track,
        audioSrc: streamUrl,
        fileId: fileId,
        playKey: playKey
      });
    } else {
      // Fallback: try to extract from drive_link
      const fileIdMatch = track.drive_link?.match(/id=([a-zA-Z0-9_-]+)/);
      const extractedFileId = fileIdMatch ? fileIdMatch[1] : null;
      
      if (extractedFileId) {
        const streamUrl = `/stream/${extractedFileId}`;
        const playKey = `${track.track_id}_${Date.now()}`;
        
        setCurrentTrack({
          ...track,
          audioSrc: streamUrl,
          fileId: extractedFileId,
          playKey: playKey
        });
      } else {
        // Last resort: open in new tab
        window.open(track.drive_link, '_blank');
      }
    }
  };

  const handleClosePlayer = () => {
    setCurrentTrack(null);
  };

  const handleRateTrack = async (trackId, rating) => {
    try {
      const response = await api.post(
        `/ratings`,
        { track_id: trackId, rating },
        { withCredentials: true }
      );
      
      // Update local state
      setUserRatings(prev => ({
        ...prev,
        [trackId]: rating
      }));
      
      // Update track ratings info
      setTrackRatings(prev => ({
        ...prev,
        [trackId]: {
          average_rating: response.data.average_rating,
          total_ratings: response.data.total_ratings
        }
      }));
    } catch (error) {
      console.error('Rating error:', error);
      alert('Error al guardar la calificación');
    }
  };

  // Favorites functions
  const toggleFavorite = async (trackId) => {
    if (togglingFavorite) return;
    
    const isFavorite = favoriteTrackIds.has(trackId);
    setTogglingFavorite(trackId);
    
    try {
      if (isFavorite) {
        await api.delete(`/favorites/${trackId}`, { withCredentials: true });
        setFavoriteTrackIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(trackId);
          return newSet;
        });
      } else {
        await api.post(`/favorites/${trackId}`, {}, { withCredentials: true });
        setFavoriteTrackIds(prev => new Set([...prev, trackId]));
      }
    } catch (error) {
      console.error('Favorite toggle error:', error);
    } finally {
      setTogglingFavorite(null);
    }
  };

  // Crate functions
  const handleOpenCratePanel = (trackId = null) => {
    setSelectedTrackForCrate(trackId);
    setShowCratePanel(true);
  };

  const handleCloseCratePanel = () => {
    setShowCratePanel(false);
    setSelectedTrackForCrate(null);
  };

  // Quick-add to playlist (admin only)
  
  const fetchCuratedPlaylists = async () => {
    try {
      const res = await api.get('/playlists/curated');
      const data = Array.isArray(res.data) ? res.data : (res.data.playlists || []);
      setCuratedPlaylists(data);
    } catch (e) { console.error('Error fetching playlists:', e); }
  };

  const handleQuickAddToPlaylist = async (playlistId, trackId) => {
    setAddingToPlaylist(playlistId);
    try {
      const res = await api.post(`/playlists/quick-add/${playlistId}`, { track_id: trackId });
      if (res.data.already_exists) {
        // Silently close — already in playlist
      }
      setQuickAddTrackId(null);
    } catch (e) {
      alert('Error: ' + (e.response?.data?.detail || e.message));
    } finally {
      setAddingToPlaylist(null);
    }
  };

  // Download queue functions
  const toggleTrackSelection = (track) => {
    setSelectedTracks(prev => {
      const isSelected = prev.some(t => t.track_id === track.track_id);
      if (isSelected) {
        return prev.filter(t => t.track_id !== track.track_id);
      } else {
        if (prev.length >= 50) {
          alert('Máximo 50 tracks por descarga');
          return prev;
        }
        return [...prev, track];
      }
    });
  };

  const isTrackSelected = (trackId) => {
    return selectedTracks.some(t => t.track_id === trackId);
  };

  const clearSelectedTracks = () => {
    setSelectedTracks([]);
  };

  const removeFromSelection = (trackId) => {
    setSelectedTracks(prev => prev.filter(t => t.track_id !== trackId));
  };

  // Version badge color helper
  const getVersionBadgeColor = (version) => {
    // Brand-strict palette: cyan accents for known versions, gray fallback
    const colors = {
      'Clean': 'bg-[#00D9FF]/15 text-[#00D9FF] border-[#00D9FF]/30',
      'Dirty': 'bg-[#1A1A1A] text-[#B3B3B3] border-[#333]',
      'Extended': 'bg-[#00D9FF]/15 text-[#00D9FF] border-[#00D9FF]/30',
      'Intro': 'bg-[#1A1A1A] text-[#B3B3B3] border-[#333]',
      'Acapella': 'bg-[#1A1A1A] text-[#B3B3B3] border-[#333]',
      'Instrumental': 'bg-[#00D9FF]/15 text-[#00D9FF] border-[#00D9FF]/30'
    };
    return colors[version] || 'bg-[#1A1A1A] text-[#888] border-[#333]';
  };

  // Download entire folder as ZIP
  const handleDownloadFolder = async () => {
    if (!selectedFolder) return;
    
    const trackCount = sortedFilteredTracks.length;
    if (!window.confirm(`¿Descargar "${selectedFolder}" como ZIP?\n\nSe creará un archivo ZIP con ${trackCount} tracks.\nEsto puede tomar unos segundos dependiendo del tamaño.`)) {
      return;
    }
    
    try {
      setDownloadingFolder(true);
      
      // Use fetch to get the ZIP file
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/downloads/folder?folder_name=${encodeURIComponent(selectedFolder)}`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            ...(localStorage.getItem('wtpl_session_token') ? { 'Authorization': `Bearer ${localStorage.getItem('wtpl_session_token')}` } : {})
          }
        }
      );
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Error al descargar');
      }
      
      // Get the filename from headers or generate one
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `WTPL_${selectedFolder}.zip`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) filename = match[1];
      }
      
      // Download the ZIP
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      alert(`Descarga completada!\n\n${filename}`);
      
    } catch (error) {
      console.error('Error downloading folder:', error);
      alert('Error al descargar carpeta: ' + error.message);
    } finally {
      setDownloadingFolder(false);
    }
  };

  // Client-side filtering (server already filters by folder, version, key, bpm, search)
  // Apply: hideDownloaded, showOnlyFavorites
  // For curated playlists, use the tracks from the playlist directly
  // OPTIMIZED: Using useMemo to avoid recalculating on every render
  const filteredTracks = useMemo(() => {
    const sourceTracts = selectedPlaylist?.tracks || tracks;
    return sourceTracts.filter(track => {
      // Hide downloaded tracks filter (client-side)
      if (hideDownloaded && downloadedTrackIds.has(track.track_id)) {
        return false;
      }
      // Show only favorites filter
      if (showOnlyFavorites && !favoriteTrackIds.has(track.track_id)) {
        return false;
      }
      return true;
    });
  }, [selectedPlaylist?.tracks, tracks, hideDownloaded, downloadedTrackIds, showOnlyFavorites, favoriteTrackIds]);
  
  // For curated playlists, tracks are already sorted by admin
  const sortedFilteredTracks = filteredTracks;

  // Total count display - use server total or filtered count
  const displayTotalCount = selectedPlaylist 
    ? sortedFilteredTracks.length 
    : (hideDownloaded || showOnlyFavorites)
    ? sortedFilteredTracks.length 
    : totalTracks;

  // Helper function to format date headers (HOY, AYER, or date)
  // Uses created_at (Drive upload date) for accurate grouping
  const getDateLabel = (date) => {
    if (!date) return 'Sin fecha';
    
    // Parse ISO string properly — append Z if no timezone indicator
    let isoStr = typeof date === 'string' ? date : date.toISOString?.() || String(date);
    if (!isoStr.includes('Z') && !isoStr.includes('+') && !isoStr.includes('-', 10)) {
      isoStr += 'Z';
    }
    
    const trackDate = new Date(isoStr);
    if (isNaN(trackDate.getTime())) return 'Sin fecha';
    
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const trackDay = new Date(trackDate.getFullYear(), trackDate.getMonth(), trackDate.getDate());
    
    if (trackDay.getTime() === today.getTime()) {
      return 'HOY';
    } else if (trackDay.getTime() === yesterday.getTime()) {
      return 'AYER';
    } else {
      return trackDate.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: '2-digit' });
    }
  };

  // Group tracks by date (using created_at = Drive upload date)
  const groupTracksByDate = (tracks) => {
    const groups = {};
    
    tracks.forEach(track => {
      const dateStr = track.created_at || track.synced_at;
      let dateKey = 'no-date';
      
      if (dateStr) {
        let isoStr = typeof dateStr === 'string' ? dateStr : String(dateStr);
        if (!isoStr.includes('Z') && !isoStr.includes('+') && !isoStr.includes('-', 10)) {
          isoStr += 'Z';
        }
        const d = new Date(isoStr);
        if (!isNaN(d.getTime())) {
          dateKey = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
        }
      }
      
      if (!groups[dateKey]) {
        groups[dateKey] = {
          key: dateKey,
          label: getDateLabel(dateStr),
          date: dateStr,
          tracks: []
        };
      }
      groups[dateKey].tracks.push(track);
    });
    
    return Object.values(groups).sort((a, b) => {
      if (!a.date) return 1;
      if (!b.date) return -1;
      return new Date(b.date) - new Date(a.date);
    });
  };

  // Group tracks when a folder or playlist is selected
  // OPTIMIZED: Using useMemo for expensive grouping operation
  const groupedTracks = useMemo(() => {
    if (!selectedFolder && !selectedPlaylist) return null;
    return groupTracksByDate(sortedFilteredTracks);
  }, [selectedFolder, selectedPlaylist, sortedFilteredTracks]);

  // Admin: fetch playlists for quick-add and close dropdown on outside click
  const isAdmin = user?.role === 'admin';
  useEffect(() => { if (isAdmin) fetchCuratedPlaylists(); }, [isAdmin]);
  useEffect(() => {
    if (!quickAddTrackId) return;
    const close = () => setQuickAddTrackId(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [quickAddTrackId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex flex-col items-center justify-center gap-4">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-[#00D9FF]"></div>
        {verifyingPayment && (
          <p className="text-[#00D9FF] text-base">Verificando tu pago de suscripción...</p>
        )}
        {verifyingFullPack && (
          <p className="text-[#00D9FF] text-base">Verificando tu compra del Full Pack...</p>
        )}
      </div>
    );
  }

  // Check if user has active subscription
  const hasActiveSubscription = subscriptionStatus?.subscription_status === 'active';
  
  // Admin bypass - allow admin to access without subscription (using role from database)

  if (!hasActiveSubscription && !isAdmin) {
    const isReturning = !!user?.has_previous_subscription;
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white">
        <div className="container mx-auto px-6 py-8">
          <div className="max-w-2xl mx-auto text-center">
            <div className="mb-8">
              <img
                src={user?.picture}
                alt={user?.name}
                className="w-20 h-20 rounded-full mx-auto mb-4 border-2 border-[#262626]"
              />
              <h2 className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>¡Hola, {user?.name}!</h2>
            </div>

            <Card className="bg-[#141414] border-[#262626]">
              <CardHeader>
                <CardTitle className="text-xl" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  {isReturning ? 'Reactiva tu Suscripción' : 'Suscripción Requerida'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-[#B3B3B3] mb-6">
                  {isReturning ? (
                    <>Vuelve a WTPL por <span className="text-[#00D9FF] font-semibold">$4.99/mes</span>. Cancela cuando quieras — sin compromiso.</>
                  ) : (
                    <>Prueba WTPL <span className="text-[#00D9FF] font-semibold">gratis por 7 días</span>. Hoy pagas $0 — luego $4.99/mes. Cancela cuando quieras.</>
                  )}
                </p>
                <div className="space-y-3">
                  <Button
                    onClick={handleSubscribe}
                    disabled={isProcessingPayment}
                    className="w-full bg-[#00D9FF] text-black font-bold hover:opacity-90 disabled:opacity-50 rounded-full"
                    data-testid="subscribe-button"
                  >
                    {isProcessingPayment
                      ? 'Procesando...'
                      : (isReturning ? 'Reactivar mi Suscripción' : 'Prueba 7 Días Gratis')}
                  </Button>
                  <PaymentMethodIcons className="py-1" showPayPal={false} />
                  <Button
                    onClick={handleLogout}
                    variant="outline"
                    className="w-full border-[#262626] hover:bg-[#1A1A1A] rounded-full"
                  >
                    Cerrar Sesión
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white pb-24">
      {/* Header - BPM Supreme Style with Animation */}
      <motion.header 
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.4 }}
        className="bg-[#0A0A0A]/95 backdrop-blur-lg border-b border-[#1A1A1A] sticky top-0 z-50"
      >
        <div className="container mx-auto px-4 h-14 flex items-center justify-between">
          <motion.div 
            className="flex items-center gap-2"
            whileHover={{ scale: 1.02 }}
          >
            <img src="/logo-icon.png" alt="WTPL" className="h-7 w-7" />
            <span className="font-bold text-lg text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>WTPL</span>
            <span className="text-[10px] text-[#00D9FF] font-bold ml-1 hidden sm:block">DJ POOL</span>
          </motion.div>
          
          {/* Center Search with Animation */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="hidden md:flex flex-1 max-w-md mx-4"
          >
            <div className="relative w-full">
              <Input
                type="text"
                placeholder="Buscar tracks, artista, BPM, key..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-[#141414] border-[#262626] text-white placeholder:text-[#555] rounded-lg pl-9 pr-3 h-9 text-sm focus:border-[#00D9FF] focus:ring-0"
              />
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#555]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <AnimatePresence>
                {searchQuery && (
                  <motion.button
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    onClick={() => setSearchQuery('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-[#555] hover:text-white text-xs"
                  >
                    ✕
                  </motion.button>
                )}
              </AnimatePresence>
            </div>
          </motion.div>

          <nav className="flex items-center gap-1">
            {user?.email === 'ydeliofleitas961204@gmail.com' && (
              <button
                onClick={handleRefreshDashboard}
                disabled={refreshingDashboard}
                data-testid="refresh-dashboard-btn"
                className="flex items-center gap-1.5 text-xs text-[#888] hover:text-[#00D9FF] bg-transparent hover:bg-[#141414] px-3 py-2 rounded-lg transition-colors disabled:opacity-50"
                title="Actualizar Dashboard"
              >
                <svg className={`w-4 h-4 ${refreshingDashboard ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
            )}
            <button
              onClick={() => setShowDownloadHistory(true)}
              className="flex items-center gap-1.5 text-xs text-[#888] hover:text-white bg-transparent hover:bg-[#141414] px-3 py-2 rounded-lg transition-colors"
              data-testid="open-history-btn"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
              <span className="hidden sm:inline">Historial</span>
            </button>
            <button
              onClick={() => handleOpenCratePanel()}
              className="flex items-center gap-1.5 text-xs text-[#888] hover:text-white bg-transparent hover:bg-[#141414] px-3 py-2 rounded-lg transition-colors"
              data-testid="open-crates-btn"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/>
              </svg>
              <span className="hidden sm:inline">Crates</span>
            </button>
            {/* Sort Selector - moved to table header */}
            <button
              onClick={() => setShowRequestForm(true)}
              className="flex items-center gap-1.5 text-xs text-[#00D9FF] hover:text-white bg-transparent hover:bg-[#00D9FF]/10 px-3 py-2 rounded-lg transition-colors"
              data-testid="request-music-btn"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
              </svg>
              <span className="hidden sm:inline">Solicitar</span>
            </button>
            <button
              onClick={() => navigate('/admin')}
              className="flex items-center gap-1.5 text-xs text-[#888] hover:text-white bg-transparent hover:bg-[#141414] px-3 py-2 rounded-lg transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
              </svg>
              <span className="hidden sm:inline">Admin</span>
            </button>
            <div className="w-px h-5 bg-[#262626] mx-1"></div>
            <button
              onClick={() => setShowSubscriptionModal(true)}
              data-testid="header-avatar"
              className="rounded-full border border-[#333] hover:border-[#00D9FF]/60 transition-colors overflow-hidden p-0"
              title="Mi Suscripción"
            >
              <img
                src={user?.picture}
                alt={user?.name}
                className="w-7 h-7 rounded-full"
              />
            </button>
            <button 
              onClick={handleLogout}
              className="flex items-center gap-1.5 text-xs text-[#888] hover:text-white bg-transparent hover:bg-[#141414] px-3 py-2 rounded-lg transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"/>
              </svg>
              <span className="hidden sm:inline">Salir</span>
            </button>
          </nav>
        </div>
      </motion.header>

      {/* Main Content with Animation */}
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.4 }}
        className="container mx-auto px-6 py-6"
      >
        <div className="grid grid-cols-12 gap-6">
          {/* Sidebar - Folders with Animation */}
          <motion.div 
            initial={{ x: -30, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.4 }}
            className="col-span-3"
          >
            <div className="sticky top-20 space-y-2 max-h-[calc(100vh-120px)] overflow-y-auto scrollbar-thin scrollbar-thumb-[#262626] scrollbar-track-transparent">
              {/* Curated Playlists */}
              <div className="bg-[#0D0D0D] border border-[#1A1A1A] rounded-xl overflow-hidden">
                <CuratedPlaylists
                  tracks={tracks}
                  selectedPlaylist={selectedPlaylist}
                  onSelectPlaylist={(playlist) => {
                    if (playlist === null) {
                      setSelectedPlaylist(null);
                    } else {
                      // Clear folder first, then set playlist
                      // This ensures the playlist tracks are shown, not the folder's
                      setSelectedFolder(null);
                      setSelectedPlaylist(playlist);
                    }
                  }}
                />
              </div>
              
              {/* Folder Tree */}
              <div className="bg-[#0D0D0D] border border-[#1A1A1A] rounded-xl overflow-hidden">
                <FolderTree
                  folders={folders}
                  selectedFolder={selectedFolder}
                  onSelectFolder={(folder) => {
                    setSelectedFolder(folder);
                    setSelectedPlaylist(null); // Clear playlist when selecting folder
                  }}
                  totalTracks={tracks.length}
                />
              </div>
            </div>
          </motion.div>

          {/* Main Content - Tracks */}
          <motion.div 
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.4 }}
            className="col-span-9"
          >
            {/* Filters Row - BPM Supreme Style */}
            <div className="mb-4">
              {/* Mobile Search */}
              <div className="md:hidden mb-4">
                <Input
                  type="text"
                  placeholder="Buscar..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full bg-[#141414] border-[#262626] text-white placeholder:text-[#666] rounded-lg h-10"
                />
              </div>
              
              <div className="flex flex-wrap gap-2 items-center">
                {/* Quick Tag Filters */}
                {Object.keys(tagCounts).length > 0 && (
                  <div className="flex flex-wrap gap-1.5 items-center" data-testid="tag-filters">
                    {/* NEW tag */}
                    {tagCounts['NEW'] > 0 && (
                      <button
                        onClick={() => { setActiveTag(activeTag === 'NEW' ? '' : 'NEW'); setVersionFilter(''); }}
                        data-testid="tag-filter-new"
                        className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold transition-all border ${
                          activeTag === 'NEW'
                            ? 'bg-[#00D9FF] text-black border-[#00D9FF] shadow-lg shadow-[#00D9FF]/20'
                            : 'bg-[#00D9FF]/10 text-[#00D9FF] border-[#00D9FF]/30 hover:bg-[#00D9FF]/20 hover:border-[#00D9FF]/50'
                        }`}
                      >
                        NEW <span className="opacity-70">{tagCounts['NEW']}</span>
                      </button>
                    )}
                    {/* TRENDING tag */}
                    {tagCounts['TRENDING'] > 0 && (
                      <button
                        onClick={() => { setActiveTag(activeTag === 'TRENDING' ? '' : 'TRENDING'); setVersionFilter(''); }}
                        data-testid="tag-filter-trending"
                        className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold transition-all border ${
                          activeTag === 'TRENDING'
                            ? 'bg-[#00D9FF] text-white border-[#00D9FF] shadow-lg shadow-[#00D9FF]/20'
                            : 'bg-[#00D9FF]/10 text-[#00D9FF] border-[#00D9FF]/30 hover:bg-[#00D9FF]/20 hover:border-[#00D9FF]/50'
                        }`}
                      >
                        TRENDING <span className="opacity-70">{tagCounts['TRENDING']}</span>
                      </button>
                    )}
                    {/* Separator */}
                    {(tagCounts['NEW'] > 0 || tagCounts['TRENDING'] > 0) && (
                      <div className="w-px h-5 bg-[#262626] mx-0.5"></div>
                    )}
                    {/* Version tags */}
                    {['Clean', 'Dirty', 'Extended', 'Intro', 'Remix', 'Mashup', 'Acapella', 'Instrumental'].filter(v => tagCounts[v]).map(ver => {
                      const tagColors = {
                        'Clean': { active: 'bg-[#00D9FF] text-black border-[#00D9FF] shadow-[#00D9FF]/20', inactive: 'bg-[#00D9FF]/10 text-[#00D9FF] border-[#00D9FF]/30 hover:bg-[#00D9FF]/20' },
                        'Dirty': { active: 'bg-[#00D9FF] text-white border-[#00D9FF] shadow-[#00D9FF]/20', inactive: 'bg-[#00D9FF]/10 text-[#00D9FF] border-[#00D9FF]/30 hover:bg-[#00D9FF]/20' },
                        'Extended': { active: 'bg-[#00D9FF] text-white border-[#00D9FF] shadow-[#00D9FF]/20', inactive: 'bg-[#00D9FF]/10 text-[#00D9FF] border-[#00D9FF]/30 hover:bg-[#00D9FF]/20' },
                        'Intro': { active: 'bg-[#00D9FF] text-black border-[#00D9FF] shadow-[#00D9FF]/20', inactive: 'bg-[#00D9FF]/10 text-[#00D9FF] border-[#00D9FF]/30 hover:bg-[#00D9FF]/20' },
                        'Remix': { active: 'bg-[#00D9FF] text-white border-[#00D9FF] shadow-[#00D9FF]/20', inactive: 'bg-[#00D9FF]/10 text-[#00D9FF] border-[#00D9FF]/30 hover:bg-[#00D9FF]/20' },
                        'Mashup': { active: 'bg-[#00D9FF] text-white border-[#00D9FF] shadow-[#00D9FF]/20', inactive: 'bg-[#00D9FF]/10 text-[#00D9FF] border-[#00D9FF]/30 hover:bg-[#00D9FF]/20' },
                        'Acapella': { active: 'bg-[#00D9FF] text-black border-[#00D9FF] shadow-[#00D9FF]/20', inactive: 'bg-[#00D9FF]/10 text-[#00D9FF] border-[#00D9FF]/30 hover:bg-[#00D9FF]/20' },
                        'Instrumental': { active: 'bg-[#00D9FF] text-black border-[#00D9FF] shadow-[#00D9FF]/20', inactive: 'bg-[#00D9FF]/10 text-[#00D9FF] border-[#00D9FF]/30 hover:bg-[#00D9FF]/20' },
                      };
                      const colors = tagColors[ver] || { active: 'bg-[#888] text-white border-[#888]', inactive: 'bg-[#888]/10 text-[#888] border-[#888]/30' };
                      return (
                        <button
                          key={ver}
                          onClick={() => { setActiveTag(activeTag === ver ? '' : ver); setVersionFilter(''); }}
                          data-testid={`tag-filter-${ver.toLowerCase()}`}
                          className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold transition-all border ${
                            activeTag === ver ? `${colors.active} shadow-lg` : colors.inactive
                          }`}
                        >
                          {ver.toUpperCase()} <span className="opacity-70">{tagCounts[ver]}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
                
                {/* Key (Camelot) Filter */}
                <select
                  value={keyFilter}
                  onChange={(e) => setKeyFilter(e.target.value)}
                  className="bg-[#141414] border border-[#262626] text-white rounded-lg px-3 h-9 text-sm focus:border-[#00D9FF] focus:ring-0 focus:outline-none cursor-pointer hover:border-[#333] transition-colors"
                  data-testid="key-filter"
                >
                  <option value="">Key</option>
                  {availableKeys.map(k => (
                    <option key={k} value={k}>{k}</option>
                  ))}
                </select>
                
                {/* Camelot Wheel Toggle */}
                <Button
                  onClick={() => setShowCamelotWheel(!showCamelotWheel)}
                  className={`h-9 px-3 rounded-lg text-sm font-medium transition-all ${showCamelotWheel ? 'bg-[#00D9FF] text-black' : 'bg-[#141414] border border-[#262626] text-[#B3B3B3] hover:text-white hover:border-[#333]'}`}
                  title="Camelot Wheel"
                  data-testid="camelot-wheel-toggle"
                >
                  🎡 Camelot
                </Button>
                
                {/* BPM Range Filter */}
                <BPMRangeFilter
                  value={bpmRange}
                  onChange={(range) => {
                    setBpmRange(range);
                    setBpmFilterActive(range[0] !== 60 || range[1] !== 200);
                  }}
                  onReset={() => {
                    setBpmRange([60, 200]);
                    setBpmFilterActive(false);
                  }}
                  isActive={bpmFilterActive}
                />
                
                {/* Hide Downloaded Toggle */}
                <motion.button
                  onClick={() => setHideDownloaded(!hideDownloaded)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                    hideDownloaded 
                      ? 'bg-[#00D9FF]/20 text-[#00D9FF] border border-[#00D9FF]/50' 
                      : 'bg-[#141414] text-[#888] border border-[#262626] hover:border-[#333]'
                  }`}
                  title={`${downloadedTrackIds.size} tracks descargados`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {hideDownloaded ? (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"/>
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                    )}
                  </svg>
                  <span>{hideDownloaded ? 'Ocultos' : 'Descargados'}</span>
                  {downloadedTrackIds.size > 0 && (
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                      hideDownloaded ? 'bg-[#00D9FF] text-black' : 'bg-[#262626] text-[#888]'
                    }`}>
                      {downloadedTrackIds.size}
                    </span>
                  )}
                </motion.button>
                
                {/* Favorites Toggle */}
                <motion.button
                  onClick={() => setShowOnlyFavorites(!showOnlyFavorites)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                    showOnlyFavorites 
                      ? 'bg-[#00D9FF]/20 text-[#00D9FF] border border-[#00D9FF]/50' 
                      : 'bg-[#141414] text-[#888] border border-[#262626] hover:border-[#333]'
                  }`}
                  title={`${favoriteTrackIds.size} tracks favoritos`}
                  data-testid="favorites-filter-btn"
                >
                  <svg className="w-4 h-4" fill={showOnlyFavorites ? "currentColor" : "none"} stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"/>
                  </svg>
                  <span>Favoritos</span>
                  {favoriteTrackIds.size > 0 && (
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                      showOnlyFavorites ? 'bg-[#00D9FF] text-white' : 'bg-[#262626] text-[#888]'
                    }`}>
                      {favoriteTrackIds.size}
                    </span>
                  )}
                </motion.button>
                
                {/* Active Filters */}
                {(searchQuery || activeTag || versionFilter || keyFilter || bpmFilterActive || hideDownloaded || showOnlyFavorites) && (
                  <div className="flex items-center gap-1.5 ml-2">
                    {searchQuery && (
                      <span 
                        className="bg-[#262626] text-white text-xs px-2 py-1 rounded cursor-pointer hover:bg-[#333]"
                        onClick={() => setSearchQuery('')}
                      >
                        {searchQuery} x
                      </span>
                    )}
                    {activeTag && (
                      <span 
                        className="bg-[#00D9FF]/20 text-[#00D9FF] text-xs px-2 py-1 rounded cursor-pointer hover:bg-[#00D9FF]/30"
                        onClick={() => setActiveTag('')}
                      >
                        {activeTag} x
                      </span>
                    )}
                    {versionFilter && !activeTag && (
                      <span 
                        className="bg-[#00D9FF]/20 text-[#00D9FF] text-xs px-2 py-1 rounded cursor-pointer hover:bg-[#00D9FF]/30"
                        onClick={() => setVersionFilter('')}
                      >
                        {versionFilter} x
                      </span>
                    )}
                    {keyFilter && (
                      <span 
                        className="bg-[#00D9FF]/20 text-[#00D9FF] text-xs px-2 py-1 rounded cursor-pointer hover:bg-[#00D9FF]/30"
                        onClick={() => setKeyFilter('')}
                      >
                        {keyFilter} ✕
                      </span>
                    )}
                    {bpmFilterActive && (
                      <span 
                        className="bg-gradient-to-r from-[#00D9FF]/20 to-[#00D9FF]/20 text-white text-xs px-2 py-1 rounded cursor-pointer hover:opacity-80"
                        onClick={() => {
                          setBpmRange([60, 200]);
                          setBpmFilterActive(false);
                        }}
                      >
                        BPM: {bpmRange[0]}-{bpmRange[1]} ✕
                      </span>
                    )}
                  </div>
                )}
                
                {/* Results count */}
                <span className="text-xs text-[#666] ml-auto">{sortedFilteredTracks.length} tracks</span>
              </div>
              
              {/* Camelot Wheel (Collapsible) */}
              {showCamelotWheel && (
                <div className="mt-4 p-4 bg-[#141414] rounded-lg border border-[#262626] animate-fade-in-down">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-white font-bold text-sm">🎡 Camelot Wheel</h3>
                    <Button
                      onClick={() => setShowCamelotWheel(false)}
                      className="text-[#666] hover:text-white h-6 w-6 p-0 rounded bg-transparent hover:bg-[#262626]"
                    >
                      ✕
                    </Button>
                  </div>
                  <p className="text-[#666] text-xs mb-3">Selecciona un key para filtrar tracks compatibles</p>
                  <div className="flex justify-center">
                    <CamelotWheel
                      selectedKey={keyFilter}
                      onSelectKey={(key) => setKeyFilter(key === keyFilter ? '' : key)}
                      showCompatible={true}
                      size="md"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Stats - WTPL Style with Animation */}
            <div className="grid grid-cols-3 gap-3 mb-4">
              {[
                { value: processingStatus?.total || totalTracks || tracks.length, label: 'Tracks', color: '#00D9FF', icon: 'M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z' },
                { value: folders.length, label: 'Carpetas', color: '#00D9FF', icon: 'M10 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2h-8l-2-2z' },
                { value: sortedFilteredTracks.length, label: 'Mostrando', color: '#00D9FF', icon: null }
              ].map((stat, i) => (
                <motion.div 
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 + (i * 0.1) }}
                  whileHover={{ scale: 1.02, borderColor: `${stat.color}60` }}
                  className="bg-[#0D0D0D] border rounded-xl px-4 py-3 flex items-center gap-3 cursor-pointer transition-colors"
                  style={{ borderColor: `${stat.color}33` }}
                >
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${stat.color}15` }}>
                    {stat.icon ? (
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" style={{ color: stat.color }}>
                        <path d={stat.icon}/>
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: stat.color }}>
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                      </svg>
                    )}
                  </div>
                  <div>
                    <motion.div 
                      className="text-xl font-bold"
                      style={{ color: stat.color }}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.7 + (i * 0.1) }}
                    >
                      {stat.value}
                    </motion.div>
                    <div className="text-[10px] text-[#555] uppercase tracking-wide">{stat.label}</div>
                  </div>
                </motion.div>
              ))}
            </div>


            {/* Hot Picks Section */}
            {!selectedFolder && !searchQuery && !versionFilter && !activeTag && (
              <>
                {/* WTPL Full Pack Promo */}
                <div className="mb-6">
                  <FullPackCard variant="dashboard" />
                </div>
                
                <HotPicks
                  onPlayTrack={handlePlayTrack}
                  onDownloadTrack={handleDownload}
                  onAddToCrate={(trackId) => handleOpenCratePanel(trackId)}
                  userRatings={userRatings}
                />
                <div className="border-t border-[#262626] my-6"></div>
              </>
            )}

            {/* Current Folder Info */}
            {selectedFolder && (
              <div className="mb-4 flex items-center justify-between bg-[#141414] rounded-lg px-4 py-3 border border-[#262626]">
                <div className="flex items-center gap-3">
                  <span className="text-[#00D9FF]"><svg className="w-4 h-4 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/></svg></span>
                  <span className="text-white font-bold text-sm">{selectedFolder}</span>
                  <span className="text-[#666] text-xs">({sortedFilteredTracks.length} tracks)</span>
                </div>
                <div className="flex items-center gap-3">
                  {/* Download Folder Button */}
                  <button 
                    onClick={handleDownloadFolder}
                    disabled={downloadingFolder || sortedFilteredTracks.length === 0}
                    className="flex items-center gap-2 bg-gradient-to-r from-[#00D9FF] to-[#00D9FF] hover:opacity-90 text-black font-semibold px-3 py-1.5 rounded-lg text-sm transition-all disabled:opacity-50"
                  >
                    {downloadingFolder ? (
                      <>
                        <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                        </svg>
                        Preparando...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
                        </svg>
                        Descargar Carpeta
                      </>
                    )}
                  </button>
                  <button 
                    onClick={() => setSelectedFolder(null)}
                    className="text-[#00D9FF] hover:text-white text-sm flex items-center gap-1"
                  >
                    ← Todos los tracks
                  </button>
                </div>
              </div>
            )}

            {/* Current Playlist Info */}
            {selectedPlaylist && (
              <motion.div 
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`mb-4 flex items-center justify-between rounded-lg px-4 py-3 border bg-gradient-to-r ${selectedPlaylist.gradient || ''} bg-opacity-10`}
                style={{ 
                  background: `linear-gradient(135deg, ${selectedPlaylist.color}15, ${selectedPlaylist.color}05)`,
                  borderColor: `${selectedPlaylist.color}40`
                }}
              >
                <div className="flex items-center gap-3">
                  <motion.span 
                    className="text-xl"
                    animate={{ rotate: [0, 10, -10, 0] }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                  >
                    {selectedPlaylist.icon}
                  </motion.span>
                  <div>
                    <span className="text-white font-bold text-sm">{selectedPlaylist.name}</span>
                    <span className="text-white/60 text-xs ml-2">• {selectedPlaylist.description || ''}</span>
                  </div>
                  <span className="text-white/80 text-xs bg-white/10 px-2 py-0.5 rounded-full">
                    {sortedFilteredTracks.length} tracks
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {/* Download Playlist Button */}
                  <DownloadPlaylistButton 
                    playlistId={selectedPlaylist.playlist_id}
                    playlistName={selectedPlaylist.name}
                    trackCount={sortedFilteredTracks.length}
                  />
                  <button 
                    onClick={() => setSelectedPlaylist(null)}
                    className="text-white/80 hover:text-white text-sm flex items-center gap-1 hover:bg-white/10 px-2 py-1 rounded-lg transition-colors"
                  >
                    ✕ Cerrar playlist
                  </button>
                </div>
              </motion.div>
            )}

            {/* BPM Supreme Style Track Table */}
            <div className="bg-[#0D0D0D] rounded-xl border border-[#1A1A1A] overflow-hidden">
              {/* Table Header */}
              <div className="grid grid-cols-12 gap-2 px-4 py-3 bg-[#0A0A0A] border-b border-[#1A1A1A] text-[10px] text-[#555] uppercase tracking-wider font-medium">
                <div className="col-span-1 flex items-center justify-center">#</div>
                <div className="col-span-3 flex items-center justify-between">
                  <span>Título / Artista</span>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    data-testid="sort-select"
                    className="bg-[#141414] border border-[#262626] text-[#888] text-[10px] font-mono rounded px-1.5 py-0.5 focus:outline-none focus:border-[#00D9FF]/50 hover:text-white hover:border-[#333] transition-colors cursor-pointer normal-case tracking-normal"
                  >
                    <option value="newest">Recientes</option>
                    <option value="oldest">Antiguos</option>
                    <option value="title_az">Título A-Z</option>
                    <option value="title_za">Título Z-A</option>
                    <option value="artist_az">Artista A-Z</option>
                    <option value="artist_za">Artista Z-A</option>
                    <option value="bpm_low">BPM ↑</option>
                    <option value="bpm_high">BPM ↓</option>
                    <option value="energy_low">Energy ↑</option>
                    <option value="energy_high">Energy ↓</option>
                  </select>
                </div>
                <div className="col-span-1 text-center">BPM</div>
                <div className="col-span-1 text-center">Key</div>
                <div className="col-span-1 text-center">Energy</div>
                <div className="col-span-2">Carpeta</div>
                <div className="col-span-1 text-center">Rating</div>
                <div className="col-span-2 text-right">Acciones</div>
              </div>

              {/* Track Rows with Animation */}
              <div className="divide-y divide-[#141414]">
                {sortedFilteredTracks.length === 0 ? (
                  <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="py-16 text-center"
                  >
                    <motion.div 
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", stiffness: 200 }}
                      className="w-16 h-16 rounded-2xl bg-[#141414] mx-auto mb-4 flex items-center justify-center"
                    >
                      <span className="text-3xl"><svg className="w-8 h-8 text-[#333] mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path d="M9 18V5l12-2v13M9 19c0 1.1-1.3 2-3 2s-3-.9-3-2 1.3-2 3-2 3 .9 3 2zm12-3c0 1.1-1.3 2-3 2s-3-.9-3-2 1.3-2 3-2 3 .9 3 2z"/></svg></span>
                    </motion.div>
                    <p className="text-white font-medium mb-2">No se encontraron tracks</p>
                    <p className="text-[#555] text-sm mb-6">
                      {selectedFolder 
                        ? `La carpeta "${selectedFolder}" está vacía.` 
                        : 'Agrega tracks desde el Panel de Admin.'}
                    </p>
                    <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                      <Button
                        onClick={() => navigate('/admin')}
                        className="bg-[#00D9FF] text-black font-bold px-6 py-2 rounded-lg hover:opacity-90"
                      >
                        + Agregar Tracks
                      </Button>
                    </motion.div>
                  </motion.div>
                ) : groupedTracks ? (
                  /* Grouped by Date Display - Enhanced Visual Separation */
                  <AnimatePresence mode="popLayout">
                    {groupedTracks.map((group, groupIndex) => {
                      const isCollapsed = collapsedDateGroups.has(group.key);
                      return (
                      <div key={group.key || group.label} className={`relative ${groupIndex > 0 ? 'mt-6' : ''}`}>
                        {/* Date Header - Enhanced (now collapsible) */}
                        <motion.div
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: groupIndex * 0.05 }}
                          className={`sticky top-0 z-10 px-4 py-3 cursor-pointer select-none ${
                            group.label === 'HOY' 
                              ? 'bg-gradient-to-r from-[#00D9FF]/10 via-[#0D0D0D] to-[#0D0D0D] border-l-4 border-[#00D9FF]' 
                              : group.label === 'AYER'
                              ? 'bg-gradient-to-r from-[#00D9FF]/10 via-[#0D0D0D] to-[#0D0D0D] border-l-4 border-[#00D9FF]'
                              : 'bg-gradient-to-r from-[#00D9FF]/5 via-[#0D0D0D] to-[#0D0D0D] border-l-4 border-[#00D9FF]/50'
                          }`}
                          onClick={() => toggleDateGroup(group.key)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleDateGroup(group.key); } }}
                          data-testid={`date-group-toggle-${group.key}`}
                          aria-expanded={!isCollapsed}
                          title={isCollapsed ? 'Expandir' : 'Colapsar'}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              {/* Chevron */}
                              <motion.svg
                                animate={{ rotate: isCollapsed ? -90 : 0 }}
                                transition={{ duration: 0.18 }}
                                className="w-4 h-4 text-[#00D9FF] flex-shrink-0"
                                fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7"/>
                              </motion.svg>
                              <div className={`px-4 py-1.5 rounded-lg text-sm font-bold ${
                                group.label === 'HOY' 
                                  ? 'bg-[#00D9FF]/20 text-[#00D9FF] shadow-lg shadow-[#00D9FF]/10' 
                                  : group.label === 'AYER'
                                  ? 'bg-[#00D9FF]/20 text-[#00D9FF] shadow-lg shadow-[#00D9FF]/10'
                                  : 'bg-[#262626] text-[#888]'
                              }`}>
                                {group.label}
                              </div>
                              <span className="text-[#666] text-sm font-medium">
                                {group.tracks.length} track{group.tracks.length !== 1 ? 's' : ''}
                              </span>
                            </div>
                            {group.label === 'HOY' && !isCollapsed && (
                              <span className="text-xs text-[#00D9FF] font-medium animate-pulse">● NUEVO</span>
                            )}
                            {/* Download all tracks from this day */}
                            {group.date && group.tracks.length > 1 && (
                              <button
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  const dateStr = typeof group.date === 'string' ? group.date.slice(0, 10) : new Date(group.date).toISOString().slice(0, 10);
                                  try {
                                    const res = await api.post('/downloads/by-date', { date: dateStr }, { responseType: 'blob', timeout: 120000 });
                                    const blob = new Blob([res.data], { type: 'application/zip' });
                                    const url = window.URL.createObjectURL(blob);
                                    const a = document.createElement('a');
                                    a.href = url;
                                    a.download = `WTPL_${dateStr.replace(/-/g, '')}_${group.tracks.length}tracks.zip`;
                                    document.body.appendChild(a);
                                    a.click();
                                    a.remove();
                                    window.URL.revokeObjectURL(url);
                                  } catch (err) {
                                    alert('Error al descargar: ' + (err.response?.data?.detail || err.message));
                                  }
                                }}
                                className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] font-mono font-semibold text-[#888] hover:text-[#00D9FF] bg-[#141414] border border-[#262626] hover:border-[#00D9FF]/30 transition-all"
                                title={`Descargar ${group.tracks.length} tracks de este día`}
                                data-testid="download-day-btn"
                              >
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                                {group.tracks.length}
                              </button>
                            )}
                          </div>
                        </motion.div>
                        
                        {/* Tracks Container with subtle background (collapsible) */}
                        <AnimatePresence initial={false}>
                          {!isCollapsed && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.2, ease: 'easeOut' }}
                              className={`overflow-hidden ${
                                group.label === 'HOY' 
                                  ? 'bg-[#00D9FF]/[0.02]' 
                                  : group.label === 'AYER'
                                  ? 'bg-[#00D9FF]/[0.02]'
                                  : ''
                              }`}
                            >
                        {/* Tracks in this group */}
                        {group.tracks.map((track, index) => {
                          const globalIndex = groupedTracks.slice(0, groupIndex).reduce((acc, g) => acc + g.tracks.length, 0) + index;
                          return (
                            <motion.div 
                              key={track.track_id}
                              initial={{ opacity: 0, x: -10 }}
                              animate={{ opacity: 1, x: 0 }}
                              exit={{ opacity: 0, x: 10 }}
                              transition={{ delay: Math.min(index * 0.02, 0.2), duration: 0.2 }}
                              whileHover={{ backgroundColor: 'rgba(255,255,255,0.03)' }}
                              className={`grid grid-cols-12 gap-2 px-4 py-3 items-center transition-colors group cursor-pointer border-b border-[#141414] ${
                                isTrackSelected(track.track_id) ? 'bg-[#00D9FF]/5 border-l-2 border-[#00D9FF]' : ''
                              }`}
                              data-testid={`track-row-${track.track_id}`}
                            >
                              {/* Column 1: Index + Play + Checkbox */}
                              <div className="col-span-1 flex items-center justify-center gap-2">
                                <div 
                                  onClick={(e) => { e.stopPropagation(); toggleTrackSelection(track); }}
                                  className={`w-5 h-5 rounded border flex items-center justify-center cursor-pointer text-xs flex-shrink-0 transition-all ${
                                    isTrackSelected(track.track_id)
                                      ? 'bg-[#00D9FF] border-[#00D9FF] text-black'
                                      : 'border-[#333] hover:border-[#00D9FF] text-[#555] group-hover:text-[#888]'
                                  }`}
                                >
                                  {isTrackSelected(track.track_id) ? '✓' : (globalIndex + 1)}
                                </div>
                                {currentTrack?.fileId === track.file_id ? (
                                  /* Now Playing Indicator - Animated Bars */
                                  <div className="w-8 h-8 rounded-full bg-[#00D9FF] flex items-center justify-center gap-0.5">
                                    <span className="w-0.5 h-3 bg-white rounded-full animate-pulse" style={{ animationDelay: '0ms' }}></span>
                                    <span className="w-0.5 h-4 bg-white rounded-full animate-pulse" style={{ animationDelay: '150ms' }}></span>
                                    <span className="w-0.5 h-2 bg-white rounded-full animate-pulse" style={{ animationDelay: '300ms' }}></span>
                                    <span className="w-0.5 h-3 bg-white rounded-full animate-pulse" style={{ animationDelay: '450ms' }}></span>
                                  </div>
                                ) : (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handlePlayTrack(track); }}
                                    className="w-8 h-8 rounded-full bg-[#00D9FF] hover:scale-110 transition-transform flex items-center justify-center opacity-0 group-hover:opacity-100"
                                    data-testid={`play-btn-${track.track_id}`}
                                  >
                                    <svg className="w-3 h-3 text-black ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                                      <path d="M8 5v14l11-7z"/>
                                    </svg>
                                  </button>
                                )}
                              </div>

                              {/* Column 2: Title & Artist */}
                              <div className="col-span-3 min-w-0" onClick={() => handlePlayTrack(track)}>
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  <span className={`font-medium text-sm truncate ${currentTrack?.fileId === track.file_id ? 'text-[#00D9FF]' : 'text-white'}`}>{track.title}</span>
                                  {currentTrack?.fileId === track.file_id && (
                                    <span className="text-[9px] px-1.5 py-0.5 rounded font-bold flex-shrink-0 bg-[#00D9FF]/20 text-[#00D9FF] animate-pulse">
                                      PLAYING
                                    </span>
                                  )}
                                  {downloadedTrackIds.has(track.track_id) && (
                                    <span className="text-[9px] px-1.5 py-0.5 rounded-full font-bold flex-shrink-0 bg-[#00D9FF]/20 text-[#00D9FF]" title="Ya descargado" data-testid={`badge-downloaded-${track.track_id}`}>
                                      DL
                                    </span>
                                  )}
                                  {/* NEW badge - track added within last 7 days */}
                                  {track.created_at && (() => {
                                    const createdDate = new Date(track.created_at);
                                    const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
                                    return createdDate > weekAgo;
                                  })() && (
                                    <span className="text-[9px] px-1.5 py-0.5 rounded-full font-bold flex-shrink-0 bg-[#00D9FF]/20 text-[#00D9FF] border border-[#00D9FF]/30" data-testid={`badge-new-${track.track_id}`}>
                                      NEW
                                    </span>
                                  )}
                                  {/* TRENDING badge */}
                                  {trendingTrackIds.has(track.track_id) && (
                                    <span className="text-[9px] px-1.5 py-0.5 rounded-full font-bold flex-shrink-0 bg-[#00D9FF]/20 text-[#00D9FF] border border-[#00D9FF]/30" data-testid={`badge-trending-${track.track_id}`}>
                                      TREND
                                    </span>
                                  )}
                                  {/* Version badge */}
                                  {track.version && (
                                    <span data-testid={`badge-version-${track.track_id}`} className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold flex-shrink-0 border ${
                                      track.version === 'Clean' ? 'bg-[#00D9FF]/15 text-[#00D9FF] border-[#00D9FF]/30'
                                        : track.version === 'Dirty' ? 'bg-[#00D9FF]/15 text-[#00D9FF] border-[#00D9FF]/30'
                                        : track.version === 'Extended' ? 'bg-[#00D9FF]/15 text-[#00D9FF] border-[#00D9FF]/30'
                                        : track.version === 'Intro' ? 'bg-[#00D9FF]/15 text-[#00D9FF] border-[#00D9FF]/30'
                                        : track.version === 'Remix' ? 'bg-[#00D9FF]/15 text-[#00D9FF] border-[#00D9FF]/30'
                                        : track.version === 'Mashup' ? 'bg-[#00D9FF]/15 text-[#00D9FF] border-[#00D9FF]/30'
                                        : track.version === 'Acapella' ? 'bg-[#00D9FF]/15 text-[#00D9FF] border-[#00D9FF]/30'
                                        : track.version === 'Instrumental' ? 'bg-[#00D9FF]/15 text-[#00D9FF] border-[#00D9FF]/30'
                                        : 'bg-[#262626] text-[#888] border-[#333]'
                                    }`}>
                                      {track.version.toUpperCase()}
                                    </span>
                                  )}
                                </div>
                                <p className="text-[#555] text-xs truncate">{track.artist}</p>
                              </div>

                              {/* Column 3: BPM */}
                              <div className="col-span-1 text-center">
                                <span className="text-[#00D9FF] font-bold text-sm">{track.bpm || '---'}</span>
                              </div>

                              {/* Column 4: Key */}
                              <div className="col-span-1 text-center">
                                {track.key ? (
                                  <button 
                                    onClick={(e) => { e.stopPropagation(); setKeyFilter(track.key); }}
                                    className="text-[#00D9FF] font-bold text-sm hover:underline"
                                  >
                                    {track.key}
                                  </button>
                                ) : (
                                  <span className="text-[#333] text-sm">---</span>
                                )}
                              </div>

                              {/* Column 5: Energy */}
                              <div className="col-span-1 text-center">
                                {track.energy_level ? (
                                  <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                                    track.energy_level >= 8 
                                      ? 'bg-[#00D9FF]/20 text-[#00D9FF]' 
                                      : track.energy_level >= 5 
                                      ? 'bg-[#00D9FF]/20 text-[#00D9FF]' 
                                      : 'bg-[#00D9FF]/20 text-[#00D9FF]'
                                  }`}>
                                    {track.energy_level}/10
                                  </span>
                                ) : (
                                  <span className="text-[#333] text-sm">---</span>
                                )}
                              </div>

                              {/* Column 6: Folder */}
                              <div className="col-span-2">
                                {track.folder && (
                                  <button 
                                    onClick={(e) => { e.stopPropagation(); setSelectedFolder(track.folder); }}
                                    className="flex items-center gap-1.5 text-[#555] text-xs hover:text-[#00D9FF] truncate group/folder"
                                  >
                                    <svg className="w-3.5 h-3.5 text-[#00D9FF] group-hover/folder:text-[#00D9FF]" fill="currentColor" viewBox="0 0 24 24">
                                      <path d="M10 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2h-8l-2-2z"/>
                                    </svg>
                                    <span className="truncate">{track.folder}</span>
                                  </button>
                                )}
                              </div>

                              {/* Column 7: Rating */}
                              <div className="col-span-1 flex justify-center" onClick={(e) => e.stopPropagation()}>
                                <StarRating
                                  rating={userRatings[track.track_id] || 0}
                                  averageRating={trackRatings[track.track_id]?.average_rating || track.average_rating || 0}
                                  totalRatings={trackRatings[track.track_id]?.total_ratings || track.total_ratings || 0}
                                  onRate={(rating) => handleRateTrack(track.track_id, rating)}
                                  size="xs"
                                />
                              </div>

                              {/* Column 8: Actions */}
                              <div className="col-span-2 flex items-center justify-end gap-2">
                                {/* Favorite Button */}
                                <button
                                  onClick={(e) => { e.stopPropagation(); toggleFavorite(track.track_id); }}
                                  disabled={togglingFavorite === track.track_id}
                                  className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${
                                    favoriteTrackIds.has(track.track_id)
                                      ? 'bg-[#00D9FF]/20 text-[#00D9FF] hover:bg-[#00D9FF]/30'
                                      : 'bg-[#141414] hover:bg-[#1A1A1A] text-[#555] hover:text-[#00D9FF] opacity-0 group-hover:opacity-100'
                                  }`}
                                  title={favoriteTrackIds.has(track.track_id) ? "Quitar de favoritos" : "Agregar a favoritos"}
                                  data-testid={`favorite-btn-${track.track_id}`}
                                >
                                  {togglingFavorite === track.track_id ? (
                                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                                    </svg>
                                  ) : (
                                    <svg className="w-4 h-4" fill={favoriteTrackIds.has(track.track_id) ? "currentColor" : "none"} stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"/>
                                    </svg>
                                  )}
                                </button>
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleOpenCratePanel(track.track_id); }}
                                  className="w-8 h-8 rounded-lg bg-[#141414] hover:bg-[#1A1A1A] flex items-center justify-center text-[#555] hover:text-[#00D9FF] transition-all opacity-0 group-hover:opacity-100"
                                  title="Agregar a Crate"
                                  data-testid={`crate-btn-${track.track_id}`}
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/>
                                  </svg>
                                </button>
                                {/* Quick Add to Playlist (admin only) */}
                                {isAdmin && (
                                  <div className="relative">
                                    <button
                                      onClick={(e) => { e.stopPropagation(); setQuickAddTrackId(quickAddTrackId === track.track_id ? null : track.track_id); }}
                                      className="w-8 h-8 rounded-lg bg-[#141414] hover:bg-[#1A1A1A] flex items-center justify-center text-[#555] hover:text-[#00D9FF] transition-all opacity-0 group-hover:opacity-100"
                                      title="Agregar a Playlist"
                                      data-testid={`playlist-add-btn-${track.track_id}`}
                                    >
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                        <path d="M12 5v14M5 12h14" strokeLinecap="round" strokeLinejoin="round"/>
                                      </svg>
                                    </button>
                                    {quickAddTrackId === track.track_id && (
                                      <div className="absolute right-0 top-10 z-50 bg-[#1A1A1A] border border-[#333] rounded-lg shadow-xl py-1 min-w-[180px] max-h-48 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                                        <p className="text-[10px] text-[#555] font-mono uppercase tracking-wider px-3 py-1.5">Agregar a playlist</p>
                                        {curatedPlaylists.map(pl => (
                                          <button
                                            key={pl.playlist_id}
                                            onClick={() => handleQuickAddToPlaylist(pl.playlist_id, track.track_id)}
                                            disabled={addingToPlaylist === pl.playlist_id}
                                            className="w-full text-left px-3 py-2 text-xs text-[#B3B3B3] hover:text-white hover:bg-[#262626] transition-colors flex items-center gap-2 disabled:opacity-50"
                                          >
                                            <span>{pl.icon || ''}</span>
                                            <span className="truncate">{pl.name}</span>
                                            {addingToPlaylist === pl.playlist_id && <span className="text-[#00D9FF] text-[10px] ml-auto">...</span>}
                                          </button>
                                        ))}
                                        {curatedPlaylists.length === 0 && <p className="text-[10px] text-[#555] px-3 py-2">Sin playlists</p>}
                                      </div>
                                    )}
                                  </div>
                                )}
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleDownload(track); }}
                                  className="flex items-center gap-2 bg-gradient-to-r from-[#00D9FF] to-[#00B8D9] hover:from-[#00B8D9] hover:to-[#00D9FF] text-black font-semibold px-4 py-2 rounded-lg text-xs transition-all hover:shadow-lg hover:shadow-[#00D9FF]/20"
                                  data-testid={`download-btn-${track.track_id}`}
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                  </svg>
                                  <span className="hidden sm:inline">Descargar</span>
                                </button>
                              </div>
                            </motion.div>
                          );
                        })}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                      );
                    })}
                  </AnimatePresence>
                ) : (
                  /* Standard List Display (no folder/playlist selected) */
                  <AnimatePresence mode="popLayout">
                    {sortedFilteredTracks.map((track, index) => (
                      <motion.div 
                        key={track.track_id}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 10 }}
                        transition={{ delay: Math.min(index * 0.02, 0.3), duration: 0.2 }}
                        whileHover={{ backgroundColor: 'rgba(255,255,255,0.03)' }}
                        className={`grid grid-cols-12 gap-2 px-4 py-3 items-center transition-colors group cursor-pointer ${
                          isTrackSelected(track.track_id) ? 'bg-[#00D9FF]/5 border-l-2 border-[#00D9FF]' : ''
                        }`}
                        data-testid={`track-row-${track.track_id}`}
                      >
                      {/* Column 1: Index + Play + Checkbox */}
                      <div className="col-span-1 flex items-center justify-center gap-2">
                        <div 
                          onClick={(e) => { e.stopPropagation(); toggleTrackSelection(track); }}
                          className={`w-5 h-5 rounded border flex items-center justify-center cursor-pointer text-xs flex-shrink-0 transition-all ${
                            isTrackSelected(track.track_id)
                              ? 'bg-[#00D9FF] border-[#00D9FF] text-black'
                              : 'border-[#333] hover:border-[#00D9FF] text-[#555] group-hover:text-[#888]'
                          }`}
                        >
                          {isTrackSelected(track.track_id) ? '✓' : (index + 1)}
                        </div>
                        {currentTrack?.fileId === track.file_id ? (
                          /* Now Playing Indicator - Animated Bars */
                          <div className="w-8 h-8 rounded-full bg-[#00D9FF] flex items-center justify-center gap-0.5">
                            <span className="w-0.5 h-3 bg-white rounded-full animate-pulse" style={{ animationDelay: '0ms' }}></span>
                            <span className="w-0.5 h-4 bg-white rounded-full animate-pulse" style={{ animationDelay: '150ms' }}></span>
                            <span className="w-0.5 h-2 bg-white rounded-full animate-pulse" style={{ animationDelay: '300ms' }}></span>
                            <span className="w-0.5 h-3 bg-white rounded-full animate-pulse" style={{ animationDelay: '450ms' }}></span>
                          </div>
                        ) : (
                          <button
                            onClick={(e) => { e.stopPropagation(); handlePlayTrack(track); }}
                            className="w-8 h-8 rounded-full bg-[#00D9FF] hover:scale-110 transition-transform flex items-center justify-center opacity-0 group-hover:opacity-100"
                            data-testid={`play-btn-${track.track_id}`}
                          >
                            <svg className="w-3 h-3 text-black ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M8 5v14l11-7z"/>
                            </svg>
                          </button>
                        )}
                      </div>

                      {/* Column 2: Title & Artist */}
                      <div className="col-span-3 min-w-0" onClick={() => handlePlayTrack(track)}>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className={`font-medium text-sm truncate ${currentTrack?.fileId === track.file_id ? 'text-[#00D9FF]' : 'text-white'}`}>{track.title}</span>
                          {currentTrack?.fileId === track.file_id && (
                            <span className="text-[9px] px-1.5 py-0.5 rounded font-bold flex-shrink-0 bg-[#00D9FF]/20 text-[#00D9FF] animate-pulse">
                              PLAYING
                            </span>
                          )}
                          {downloadedTrackIds.has(track.track_id) && (
                            <span className="text-[9px] px-1.5 py-0.5 rounded-full font-bold flex-shrink-0 bg-[#00D9FF]/20 text-[#00D9FF]" title="Ya descargado">
                              DL
                            </span>
                          )}
                          {track.created_at && (() => {
                            const createdDate = new Date(track.created_at);
                            const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
                            return createdDate > weekAgo;
                          })() && (
                            <span className="text-[9px] px-1.5 py-0.5 rounded-full font-bold flex-shrink-0 bg-[#00D9FF]/20 text-[#00D9FF] border border-[#00D9FF]/30">
                              NEW
                            </span>
                          )}
                          {trendingTrackIds.has(track.track_id) && (
                            <span className="text-[9px] px-1.5 py-0.5 rounded-full font-bold flex-shrink-0 bg-[#00D9FF]/20 text-[#00D9FF] border border-[#00D9FF]/30">
                              TREND
                            </span>
                          )}
                          {track.version && (
                            <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold flex-shrink-0 border ${
                              track.version === 'Clean' ? 'bg-[#00D9FF]/15 text-[#00D9FF] border-[#00D9FF]/30'
                                : track.version === 'Dirty' ? 'bg-[#00D9FF]/15 text-[#00D9FF] border-[#00D9FF]/30'
                                : track.version === 'Extended' ? 'bg-[#00D9FF]/15 text-[#00D9FF] border-[#00D9FF]/30'
                                : track.version === 'Intro' ? 'bg-[#00D9FF]/15 text-[#00D9FF] border-[#00D9FF]/30'
                                : track.version === 'Remix' ? 'bg-[#00D9FF]/15 text-[#00D9FF] border-[#00D9FF]/30'
                                : track.version === 'Mashup' ? 'bg-[#00D9FF]/15 text-[#00D9FF] border-[#00D9FF]/30'
                                : track.version === 'Acapella' ? 'bg-[#00D9FF]/15 text-[#00D9FF] border-[#00D9FF]/30'
                                : track.version === 'Instrumental' ? 'bg-[#00D9FF]/15 text-[#00D9FF] border-[#00D9FF]/30'
                                : 'bg-[#262626] text-[#888] border-[#333]'
                            }`}>
                              {track.version.toUpperCase()}
                            </span>
                          )}
                        </div>
                        <p className="text-[#555] text-xs truncate">{track.artist}</p>
                      </div>

                      {/* Column 3: BPM */}
                      <div className="col-span-1 text-center">
                        <span className="text-[#00D9FF] font-bold text-sm">{track.bpm || '---'}</span>
                      </div>

                      {/* Column 4: Key */}
                      <div className="col-span-1 text-center">
                        {track.key ? (
                          <button 
                            onClick={(e) => { e.stopPropagation(); setKeyFilter(track.key); }}
                            className="text-[#00D9FF] font-bold text-sm hover:underline"
                          >
                            {track.key}
                          </button>
                        ) : (
                          <span className="text-[#333] text-sm">---</span>
                        )}
                      </div>

                      {/* Column 5: Energy */}
                      <div className="col-span-1 text-center">
                        {track.energy_level ? (
                          <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                            track.energy_level >= 8 
                              ? 'bg-[#00D9FF]/20 text-[#00D9FF]' 
                              : track.energy_level >= 5 
                              ? 'bg-[#00D9FF]/20 text-[#00D9FF]' 
                              : 'bg-[#00D9FF]/20 text-[#00D9FF]'
                          }`}>
                            {track.energy_level}/10
                          </span>
                        ) : (
                          <span className="text-[#333] text-sm">---</span>
                        )}
                      </div>

                      {/* Column 6: Folder */}
                      <div className="col-span-2">
                        {track.folder && (
                          <button 
                            onClick={(e) => { e.stopPropagation(); setSelectedFolder(track.folder); }}
                            className="flex items-center gap-1.5 text-[#555] text-xs hover:text-[#00D9FF] truncate group/folder"
                          >
                            <svg className="w-3.5 h-3.5 text-[#00D9FF] group-hover/folder:text-[#00D9FF]" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M10 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2h-8l-2-2z"/>
                            </svg>
                            <span className="truncate">{track.folder}</span>
                          </button>
                        )}
                      </div>

                      {/* Column 7: Rating */}
                      <div className="col-span-1 flex justify-center" onClick={(e) => e.stopPropagation()}>
                        <StarRating
                          rating={userRatings[track.track_id] || 0}
                          averageRating={trackRatings[track.track_id]?.average_rating || track.average_rating || 0}
                          totalRatings={trackRatings[track.track_id]?.total_ratings || track.total_ratings || 0}
                          onRate={(rating) => handleRateTrack(track.track_id, rating)}
                          size="xs"
                        />
                      </div>

                      {/* Column 8: Actions */}
                      <div className="col-span-2 flex items-center justify-end gap-2">
                        {/* Favorite Button */}
                        <button
                          onClick={(e) => { e.stopPropagation(); toggleFavorite(track.track_id); }}
                          disabled={togglingFavorite === track.track_id}
                          className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${
                            favoriteTrackIds.has(track.track_id)
                              ? 'bg-[#00D9FF]/20 text-[#00D9FF] hover:bg-[#00D9FF]/30'
                              : 'bg-[#141414] hover:bg-[#1A1A1A] text-[#555] hover:text-[#00D9FF] opacity-0 group-hover:opacity-100'
                          }`}
                          title={favoriteTrackIds.has(track.track_id) ? "Quitar de favoritos" : "Agregar a favoritos"}
                          data-testid={`favorite-btn-${track.track_id}`}
                        >
                          {togglingFavorite === track.track_id ? (
                            <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                            </svg>
                          ) : (
                            <svg className="w-4 h-4" fill={favoriteTrackIds.has(track.track_id) ? "currentColor" : "none"} stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"/>
                            </svg>
                          )}
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleOpenCratePanel(track.track_id); }}
                          className="w-8 h-8 rounded-lg bg-[#141414] hover:bg-[#1A1A1A] flex items-center justify-center text-[#555] hover:text-[#00D9FF] transition-all opacity-0 group-hover:opacity-100"
                          title="Agregar a Crate"
                          data-testid={`crate-btn-${track.track_id}`}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/>
                          </svg>
                        </button>
                        {/* Quick Add to Playlist (admin only) */}
                        {isAdmin && (
                          <div className="relative">
                            <button
                              onClick={(e) => { e.stopPropagation(); setQuickAddTrackId(quickAddTrackId === track.track_id ? null : track.track_id); }}
                              className="w-8 h-8 rounded-lg bg-[#141414] hover:bg-[#1A1A1A] flex items-center justify-center text-[#555] hover:text-[#00D9FF] transition-all opacity-0 group-hover:opacity-100"
                              title="Agregar a Playlist"
                              data-testid={`playlist-add-btn-${track.track_id}`}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                <path d="M12 5v14M5 12h14" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </button>
                            {quickAddTrackId === track.track_id && (
                              <div className="absolute right-0 top-10 z-50 bg-[#1A1A1A] border border-[#333] rounded-lg shadow-xl py-1 min-w-[180px] max-h-48 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                                <p className="text-[10px] text-[#555] font-mono uppercase tracking-wider px-3 py-1.5">Agregar a playlist</p>
                                {curatedPlaylists.map(pl => (
                                  <button
                                    key={pl.playlist_id}
                                    onClick={() => handleQuickAddToPlaylist(pl.playlist_id, track.track_id)}
                                    disabled={addingToPlaylist === pl.playlist_id}
                                    className="w-full text-left px-3 py-2 text-xs text-[#B3B3B3] hover:text-white hover:bg-[#262626] transition-colors flex items-center gap-2 disabled:opacity-50"
                                  >
                                    <span>{pl.icon || ''}</span>
                                    <span className="truncate">{pl.name}</span>
                                    {addingToPlaylist === pl.playlist_id && <span className="text-[#00D9FF] text-[10px] ml-auto">...</span>}
                                  </button>
                                ))}
                                {curatedPlaylists.length === 0 && <p className="text-[10px] text-[#555] px-3 py-2">Sin playlists</p>}
                              </div>
                            )}
                          </div>
                        )}
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDownload(track); }}
                          className="flex items-center gap-2 bg-gradient-to-r from-[#00D9FF] to-[#00B8D9] hover:from-[#00B8D9] hover:to-[#00D9FF] text-black font-semibold px-4 py-2 rounded-lg text-xs transition-all hover:shadow-lg hover:shadow-[#00D9FF]/20"
                          data-testid={`download-btn-${track.track_id}`}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                          </svg>
                          <span className="hidden sm:inline">Descargar</span>
                        </button>
                      </div>
                    </motion.div>
                  ))}
                  </AnimatePresence>
                )}
              </div>
              
              {/* Traditional Pagination Controls */}
              {totalPages > 1 && !selectedPlaylist && (
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="py-6 flex flex-col items-center gap-4 border-t border-[#1A1A1A] bg-[#0D0D0D]"
                >
                  <div className="flex items-center gap-2 text-[#666] text-sm">
                    <span>Mostrando {((currentPage - 1) * PAGE_SIZE) + 1} - {Math.min(currentPage * PAGE_SIZE, totalTracks)} de {totalTracks} tracks</span>
                  </div>
                  
                  {/* Pagination Buttons */}
                  <div className="flex items-center gap-2">
                    {/* Previous Button */}
                    <button
                      onClick={() => goToPage(currentPage - 1)}
                      disabled={currentPage === 1 || loadingMore}
                      className={`px-3 py-2 rounded-lg font-medium text-sm transition-all ${
                        currentPage === 1 
                          ? 'bg-[#1A1A1A] text-[#444] cursor-not-allowed' 
                          : 'bg-[#1A1A1A] text-white hover:bg-[#262626]'
                      }`}
                    >
                      ← Anterior
                    </button>
                    
                    {/* Page Numbers */}
                    <div className="flex items-center gap-1">
                      {getPageNumbers().map((page, idx) => (
                        page === '...' ? (
                          <span key={`ellipsis-${idx}`} className="px-2 text-[#555]">...</span>
                        ) : (
                          <button
                            key={page}
                            onClick={() => goToPage(page)}
                            disabled={loadingMore}
                            className={`w-10 h-10 rounded-lg font-medium text-sm transition-all ${
                              page === currentPage
                                ? 'bg-[#00D9FF] text-black'
                                : 'bg-[#1A1A1A] text-white hover:bg-[#262626]'
                            }`}
                          >
                            {page}
                          </button>
                        )
                      ))}
                    </div>
                    
                    {/* Next Button */}
                    <button
                      onClick={() => goToPage(currentPage + 1)}
                      disabled={currentPage === totalPages || loadingMore}
                      className={`px-3 py-2 rounded-lg font-medium text-sm transition-all ${
                        currentPage === totalPages 
                          ? 'bg-[#1A1A1A] text-[#444] cursor-not-allowed' 
                          : 'bg-[#1A1A1A] text-white hover:bg-[#262626]'
                      }`}
                    >
                      Siguiente →
                    </button>
                  </div>
                  
                  {/* Loading indicator */}
                  {loadingMore && (
                    <div className="flex items-center gap-2 text-[#00D9FF]">
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                      </svg>
                      <span className="text-sm">Cargando página...</span>
                    </div>
                  )}
                </motion.div>
              )}
            </div>
          </motion.div>
        </div>
      </motion.div>
      
      {/* Waveform Audio Player - Sticky Bottom with Animation */}
      <AnimatePresence>
        {currentTrack && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
          >
            <SimpleAudioPlayer
              key={currentTrack.playKey}
              fileId={currentTrack.fileId}
              trackTitle={currentTrack.title}
              artist={currentTrack.artist}
              onClose={handleClosePlayer}
              trackInfo={{
                key: currentTrack.key,
                bpm: currentTrack.bpm,
                version: currentTrack.version
              }}
            />
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Download Queue */}
      <DownloadQueue
        selectedTracks={selectedTracks}
        onClearSelection={clearSelectedTracks}
        onRemoveTrack={removeFromSelection}
      />
      
      {/* Crate Panel */}
      <AnimatePresence>
        {showCratePanel && (
          <motion.div
            initial={{ x: 300, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 300, opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
          >
            <CratePanel
              isOpen={showCratePanel}
              onClose={handleCloseCratePanel}
              selectedTrackId={selectedTrackForCrate}
              onAddTrack={() => {
                // Refresh data if needed
              }}
            />
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Download History Modal */}
      <AnimatePresence>
        {showDownloadHistory && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2 }}
          >
            <DownloadHistory
              onClose={() => setShowDownloadHistory(false)}
              onRedownload={(track) => {
                // Track redownloaded
              }}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Music Request Form Modal */}
      <AnimatePresence>
        {showRequestForm && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2 }}
          >
            <MusicRequestForm
              onClose={() => setShowRequestForm(false)}
              onSuccess={() => setShowRequestForm(false)}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* My Subscription Modal */}
      <AnimatePresence>
        {showSubscriptionModal && (
          <MySubscriptionModal onClose={() => setShowSubscriptionModal(false)} />
        )}
      </AnimatePresence>
    </div>
  );
};

export default Dashboard;
